import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, SparklesIcon, BotIcon, SearchIcon, Loader2Icon, CheckCircle2Icon, ChevronLeftIcon, ChevronRightIcon, TargetIcon } from 'lucide-react';
import { Button } from '../ui/Button';
export function OpenclawHero() {
  return <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8" data-id="element-5477">
      <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 p-8 md:p-12 lg:p-16 relative overflow-hidden" data-id="element-5478">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] bg-blue-50/50 rounded-full blur-3xl -z-10 pointer-events-none" data-id="element-5479" />

        <div className="relative z-10" data-id="element-5480">
          <a href="#/features" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-slate-900 transition-colors mb-8" data-id="element-5481">
            <ArrowLeftIcon className="w-4 h-4" data-id="element-5482" /> All features
          </a>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-5483">
            {/* Left Column: Copy & CTAs */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} className="max-w-2xl" data-id="element-5484">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-full text-sm font-bold mb-6 border border-blue-100" data-id="element-5485">
                <SparklesIcon className="w-4 h-4" data-id="element-5486" />
                Openclaw for sales
              </div>

              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-slate-900 mb-8 leading-[1.05]" data-id="element-5487">
                Your AI agent for <br data-id="element-5488" />
                <span className="text-blue-600" data-id="element-5489">local sales prospecting</span>
              </h1>

              <p className="text-xl sm:text-2xl text-slate-600 mb-8 max-w-lg leading-relaxed" data-id="element-5490">
                Stop manually prospecting. Our AI agent finds and scores leads
                across 4 dimensions—revenue potential, intent to buy, ICP match,
                and tech readiness—then drafts personalized outreach emails
                automatically.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-10" data-id="element-5491">
                <Button href="http://app.openmart.com/register" variant="primary" size="lg" arrow data-id="element-5492">
                  Try for free
                </Button>
              </div>

              {/* Static Stats Bar */}
              <div className="flex gap-12 pt-8 border-t border-slate-100" data-id="element-5493">
                <div data-id="element-5494">
                  <div className="text-3xl font-bold text-slate-900 mb-1" data-id="element-5495">
                    200M+
                  </div>
                  <div className="text-sm text-slate-500 font-medium" data-id="element-5496">
                    Local businesses
                  </div>
                </div>
                <div data-id="element-5497">
                  <div className="text-3xl font-bold text-slate-900 mb-1" data-id="element-5498">
                    4 dimensions
                  </div>
                  <div className="text-sm text-slate-500 font-medium" data-id="element-5499">
                    Weighted scoring
                  </div>
                </div>
                <div data-id="element-5500">
                  <div className="text-3xl font-bold text-slate-900 mb-1" data-id="element-5501">
                    AI-drafted
                  </div>
                  <div className="text-sm text-slate-500 font-medium" data-id="element-5502">
                    Personalized emails
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Right Column: Visual */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="relative" data-id="element-5503">
              <div className="bg-slate-50 rounded-2xl border border-slate-200 shadow-xl overflow-hidden flex flex-col h-[500px]" data-id="element-5504">
                {/* Chat Header */}
                <div className="px-4 py-3 border-b border-stone-200/50 flex items-center justify-between bg-white/50" data-id="element-5505">
                  <div className="flex items-center gap-2" data-id="element-5506">
                    <span className="text-sm font-semibold text-stone-800" data-id="element-5507">
                      New sales search
                    </span>
                    <ChevronRightIcon className="w-4 h-4 text-stone-400 rotate-90" data-id="element-5508" />
                  </div>
                </div>

                {/* Chat Messages */}
                <div className="p-5 space-y-6 flex-1 overflow-y-auto" data-id="element-5509">
                  {/* User Message */}
                  <div className="flex justify-end" data-id="element-5510">
                    <div className="bg-stone-800 text-white rounded-2xl rounded-tr-sm px-5 py-3.5 text-sm max-w-[85%] leading-relaxed shadow-sm" data-id="element-5511">
                      Find hair salons in San Francisco with 2+ locations
                    </div>
                  </div>

                  {/* Agent Action Line */}
                  <motion.div initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} transition={{
                  delay: 0.8
                }} className="flex items-center gap-2 text-xs text-stone-500 pl-1" data-id="element-5512">
                    <ChevronRightIcon className="w-3.5 h-3.5" data-id="element-5513" />
                    Searching 200M+ businesses...
                  </motion.div>

                  {/* Agent Response */}
                  <motion.div initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: 1.3,
                  duration: 0.5
                }} className="flex items-start gap-3" data-id="element-5514">
                    <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center shrink-0 mt-0.5" data-id="element-5515">
                      <SparklesIcon className="w-3.5 h-3.5 text-blue-600" data-id="element-5516" />
                    </div>
                    <div className="flex-1 space-y-3" data-id="element-5517">
                      <p className="text-sm text-stone-800 leading-relaxed" data-id="element-5518">
                        I found <strong data-id="element-5519">28 qualified leads</strong> matching
                        your criteria. Here are the top results scored by
                        revenue potential and intent:
                      </p>

                      <div className="bg-white rounded-xl border border-stone-200 shadow-sm overflow-hidden" data-id="element-5520">
                        <div className="space-y-0 divide-y divide-stone-100" data-id="element-5521">
                          {[{
                          name: 'Supercuts',
                          domain: 'supercuts.com',
                          size: '50–200',
                          score: 94
                        }, {
                          name: 'Sephora',
                          domain: 'sephora.com',
                          size: '200+',
                          score: 91
                        }, {
                          name: 'SmartStyle',
                          domain: 'smartstyle.com',
                          size: '50–200',
                          score: 87
                        }].map((lead, i) => <div key={i} className="flex items-center gap-3 px-3 py-2.5 hover:bg-stone-50/50 transition-colors" data-id="element-5522">
                              <span className="text-sm font-semibold text-stone-800 w-[80px] shrink-0" data-id="element-5523">
                                {lead.name}
                              </span>
                              <span className="text-xs text-stone-400 truncate flex-1" data-id="element-5524">
                                {lead.domain}
                              </span>
                              <span className="text-xs text-stone-500 shrink-0" data-id="element-5525">
                                {lead.size}
                              </span>
                              <span className={`text-xs font-bold px-2.5 py-1 rounded-full shrink-0 ${lead.score >= 90 ? 'bg-blue-100 text-blue-700' : 'bg-blue-50 text-blue-600'}`} data-id="element-5526">
                                {lead.score}
                              </span>
                            </div>)}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </div>

                {/* Chat Input */}
                <div className="p-4 border-t border-stone-200/50 bg-white/50" data-id="element-5527">
                  <div className="relative" data-id="element-5528">
                    <input type="text" placeholder="Ask a follow-up question..." className="w-full pl-4 pr-12 py-3 rounded-xl border border-stone-200 bg-white text-sm focus:outline-none focus:border-stone-300 focus:ring-1 focus:ring-stone-300 placeholder:text-stone-400" readOnly data-id="element-5529" />
                    <div className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-stone-100 rounded-lg flex items-center justify-center" data-id="element-5530">
                      <ArrowLeftIcon className="w-4 h-4 text-stone-400 rotate-90" data-id="element-5531" />
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}